import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { shuffleArray } from "../utils/utils";
import "./index.css";

// Reusable WhatsApp Icon Component
const WhatsAppIcon = ({ className, fill = "currentColor" }) => (
  <svg
    className={className}
    viewBox="0 0 32 32"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M16 0C7.164 0 0 7.164 0 16c0 2.825.738 5.478 2.031 7.781L.067 31.5l8.031-2.109A15.927 15.927 0 0 0 16 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.333c-2.604 0-5.083-.739-7.167-2.021l-.5-.313-5.188 1.354 1.396-5.083-.344-.521A13.244 13.244 0 0 1 2.667 16c0-7.364 5.969-13.333 13.333-13.333S29.333 8.636 29.333 16 23.364 29.333 16 29.333z" />
    <path d="M23.271 19.229c-.396-.198-2.344-1.156-2.708-1.292-.365-.135-.63-.198-.896.198-.266.396-1.031 1.292-1.266 1.563-.234.271-.469.302-.865.104-.396-.198-1.672-.615-3.182-1.958-1.177-1.042-1.969-2.333-2.198-2.729-.229-.396-.025-.609.177-.802.177-.177.396-.458.594-.687.198-.229.266-.396.396-.667.135-.271.073-.5-.031-.698-.104-.198-.896-2.156-1.229-2.948-.323-.771-.656-.667-.896-.677-.234-.01-.5-.01-.766-.01s-.698.094-1.063.5c-.365.396-1.396 1.365-1.396 3.323s1.427 3.854 1.625 4.115c.198.266 2.792 4.26 6.76 5.969.943.406 1.677.646 2.25.823.948.302 1.813.26 2.5.156.76-.115 2.344-.958 2.677-1.885.333-.927.333-1.719.234-1.885-.104-.167-.365-.271-.766-.469z" />
  </svg>
);

const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [supportTeam, setSupportTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSupportTeam = async () => {
      setLoading(true);
      try {
        // Fetch users from "users" collection (matches admin panel)
        const querySnapshot = await getDocs(collection(db, "users"));

        const teamData = querySnapshot.docs
          .map((doc) => {
            const data = doc.data();
            // Map admin panel field names to widget field names
            return {
              id: doc.id,
              name: data.name,
              role: data.designation, // Map designation → role
              city: data.city, // City field
              whatsapp: data.whatsapp_no, // Map whatsapp_no → whatsapp
              defaultMessage: data.defaultMessage || "Hi There!",
              order: data.order || 0,
            };
          })
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        // Shuffle team members for fair distribution
        setSupportTeam(shuffleArray(teamData));
      } catch (error) {
        console.error("Error fetching support team:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSupportTeam();
  }, []);

  const toggleWidget = () => {
    setIsOpen(!isOpen);
  };

  const handleWhatsAppClick = (member) => {
    if (!member.whatsapp) {
      console.error("No WhatsApp number for member:", member.name);
      alert("WhatsApp number not available for this member.");
      return;
    }

    const defaultMessage = member.defaultMessage || "Hi There!";
    const encodedMessage = encodeURIComponent(defaultMessage);
    const whatsappLink = `https://wa.me/${member.whatsapp}?text=${encodedMessage}`;

    window.open(whatsappLink, "_blank");
  };

  return (
    <>
      <div className="whatsapp-widget-container">
        {!isOpen && <p>Need help? Chat with Us</p>}
        {/* Floating WhatsApp Button */}
        <button
          className="whatsapp-float-button"
          onClick={toggleWidget}
          aria-label="Open WhatsApp Chat Support"
        >
          <WhatsAppIcon className="whatsapp-icon" />
        </button>

        {/* Modal Panel */}
        {isOpen && (
          <>
            <div className="whatsapp-modal">
              {/* Header Section (Green Banner) */}
              <div className="whatsapp-modal-header">
                <WhatsAppIcon className="whatsapp-header-icon" fill="white" />
                <div className="whatsapp-header-content">
                  <h3 className="whatsapp-header-title-h3">Start a Conversation</h3>
                  <p className="whatsapp-header-subtitle">Hi! Click one of our member below to chat on <span className="whatsapp-bold">Whatsapp</span> !</p>
                </div>
              </div>

              {/* Sub-header Text */}
              {/* <div className="whatsapp-subheader">
                <p>Hi! Click one of our member below to chat on WhatsApp ;)</p>
              </div> */}

              {/* Modal Body */}
              <div className="whatsapp-modal-body">
                {loading ? (
                  <div className="whatsapp-loading">
                    <div className="spinner"></div>
                    <p>Loading support team...</p>
                  </div>
                ) : supportTeam.length === 0 ? (
                  <div className="whatsapp-empty">
                    <p>No support members available at the moment.</p>
                  </div>
                ) : (
                  <div className="whatsapp-team-list">
                    {supportTeam.map((member) => (
                      <button
                        key={member.id}
                        className="whatsapp-team-member"
                        onClick={() => handleWhatsAppClick(member)}
                      >
                        {/* WhatsApp Icon Circle */}
                        <div className="member-whatsapp-icon">
                          <WhatsAppIcon fill="white" />
                        </div>

                        {/* Member Info */}
                        <div className="member-info">
                          <h4 className="member-name">{member.name}</h4>
                          {member.role && (
                            <p className="member-role">
                              {member.role}
                              {member.city && `, ${member.city}`}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Close Button (Floating inside modal) */}
              <button
                className="whatsapp-close-button"
                onClick={toggleWidget}
                aria-label="Close"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Backdrop */}
            <div className="whatsapp-backdrop" onClick={toggleWidget}></div>
          </>
        )}
      </div>
    </>
  );
};

export default WhatsAppWidget;
